import { useEffect, useRef } from "react";
import { X, TrendingUp, TrendingDown, AlertCircle, CheckCircle, Bell } from "lucide-react";
import { useNotificationStore } from "../../stores/notificationStore";

const TOAST_DURATION = 5000;

const TYPE_STYLES = {
  success: {
    bg: "bg-accent-green/10 border-accent-green/30",
    icon: CheckCircle,
    iconColor: "text-accent-green",
  },
  error: {
    bg: "bg-accent-red/10 border-accent-red/30",
    icon: AlertCircle,
    iconColor: "text-accent-red",
  },
  trade: {
    bg: "bg-accent-blue/10 border-accent-blue/30",
    icon: TrendingUp,
    iconColor: "text-accent-blue",
  },
  signal: {
    bg: "bg-accent-yellow/10 border-accent-yellow/30",
    icon: Bell,
    iconColor: "text-accent-yellow",
  },
  info: {
    bg: "bg-dark-600/80 border-dark-500",
    icon: Bell,
    iconColor: "text-text-muted",
  },
};

function Toast({ notification, onDismiss }) {
  const timerRef = useRef(null);
  const style = TYPE_STYLES[notification.type] || TYPE_STYLES.info;
  const Icon = style.icon;

  useEffect(() => {
    timerRef.current = setTimeout(() => onDismiss(notification.id), TOAST_DURATION);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [notification.id, onDismiss]);

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg backdrop-blur-sm animate-in slide-in-from-right ${style.bg}`}
      role="alert"
    >
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.iconColor}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-white">{notification.title}</p>
        {notification.message && (
          <p className="mt-0.5 text-xs text-text-muted">{notification.message}</p>
        )}
      </div>
      <button
        onClick={() => onDismiss(notification.id)}
        className="shrink-0 text-text-muted hover:text-white"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function ToastProvider({ children }) {
  const notifications = useNotificationStore((s) => s.notifications);
  const removeNotification = useNotificationStore((s) => s.removeNotification);

  // Show only the 5 most recent unread toasts
  const visibleToasts = notifications.filter((n) => !n.read).slice(0, 5);

  return (
    <>
      {children}

      {/* Toast container — bottom-right on desktop, top on mobile */}
      <div className="pointer-events-none fixed inset-x-0 top-4 z-50 flex flex-col items-center gap-2 px-4 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:top-auto sm:items-end sm:px-0">
        {visibleToasts.map((n) => (
          <div key={n.id} className="pointer-events-auto w-full max-w-sm">
            <Toast notification={n} onDismiss={removeNotification} />
          </div>
        ))}
      </div>
    </>
  );
}
